'use strict';
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createAsyncAction = undefined;

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _reduxActions = require('redux-actions');

var _invariant = require('../lib/invariant');

var _invariant2 = _interopRequireDefault(_invariant);

var _isActionType = require('../lib/isActionType');

var _isActionType2 = _interopRequireDefault(_isActionType);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var isFunction = function isFunction(maybeFn) {
  return typeof maybeFn === 'function';
};
var isObject = function isObject(maybeObj) {
  return (typeof maybeObj === 'undefined' ? 'undefined' : _typeof(maybeObj)) === 'object' && !Array.isArray(maybeObj);
};
var areValidActionTypes = function areValidActionTypes(actionTypes) {
  return isObject(actionTypes) && (0, _isActionType2.default)(actionTypes.SUCCEEDED) && (0, _isActionType2.default)(actionTypes.STARTED) && (0, _isActionType2.default)(actionTypes.FAILED);
};

/**
 * createAsyncAction is a utility for creating generic thunks that
 * make some asynchronous request.
 *
 * Use Cases:
 * - Fetch an individual record with a custom error metadata builder
 * - Fetch a list of records
 *
 * @param {Object|Map} props
 * @param {Object} [props.actionTypes] an object containing string values for 'SUCCEEDED',
 *                                     'FAILED', and 'STARTED' keys.
 * @param {Function} [props.requestFn] a request function that returns a promise
 * @param {Function} [props.toRecordFn] converts the resolved value from requestFn to a record
 * @param {Function} [props.failureMetaActionCreator] builds a `meta` object on failure
 * @param {Function} [props.successMetaActionCreator] builds a `meta` object on success
 *
 * @example <caption>An individual record is fetched</caption>
 * const Thread = Record({threadId: null})
 * const fetchThread = createAsyncAction({
 *   actionTypes: {
 *     SUCCEEDED: 'FETCH_THREAD_SUCCEEDED',
 *     STARTED: 'FETCH_THREAD_STARTED',
 *     FAILED: "FETCH_THREAD_FAILED",
 *   },
 *   requetsFn: fetchThreadClient,
 *   toRecordFn: Thread,
 *   failureMetaActionCreator: (error) => {
 *     if(error.status === 404) {
 *       return alertMeta
 *     }
 *   }
 * })
 *
 * dispatch(fetchThread({threadId: 123}))
 *
 * @example <caption>A list of records is fetched</caption>
 * const Thread = Record({threadId: null})
 * const fetchThreads = createAsyncAction({
 *   actionTypes: {
 *     SUCCEEDED: 'FETCH_THREADS_SUCCEEDED',
 *     STARTED: 'FETCH_THREADS_STARTED',
 *     FAILED: "FETCH_THREADS_FAILED",
 *   },
 *   requetsFn: fetchThreadsClient,
 *   toRecordFn: (threads) => List(threads.map(Thread)),
 * })
 *
 * dispatch(fetchThreads({threadListId: 321}))
 */
var createAsyncAction = exports.createAsyncAction = function createAsyncAction(_ref) {
  var actionTypes = _ref.actionTypes,
      requestFn = _ref.requestFn,
      toRecordFn = _ref.toRecordFn,
      _ref$failureMetaActio = _ref.failureMetaActionCreator,
      failureMetaActionCreator = _ref$failureMetaActio === undefined ? function () {
    return {};
  } : _ref$failureMetaActio,
      _ref$successMetaActio = _ref.successMetaActionCreator,
      successMetaActionCreator = _ref$successMetaActio === undefined ? function () {
    return {};
  } : _ref$successMetaActio;

  (0, _invariant2.default)(areValidActionTypes(actionTypes), 'createAsyncAction expected actionTypes to be an Object containing valid type strings for keys: "FAILED", "STARTED", and "SUCCEEDED". Got: ' + actionTypes);
  (0, _invariant2.default)(isFunction(requestFn), 'createAsyncAction Expected requestFn to be a Function. Got: "' + requestFn + '"');
  (0, _invariant2.default)(isFunction(toRecordFn), 'createAsyncAction expected toRecordFn to be a Function. Got: "' + toRecordFn + '"');

  var STARTED = actionTypes.STARTED,
      SUCCEEDED = actionTypes.SUCCEEDED,
      FAILED = actionTypes.FAILED;


  var startedAction = (0, _reduxActions.createAction)(STARTED);
  var succeededAction = (0, _reduxActions.createAction)(SUCCEEDED, function (payload) {
    return payload;
  }, successMetaActionCreator);
  var failedAction = (0, _reduxActions.createAction)(FAILED, function (payload) {
    return payload;
  }, failureMetaActionCreator);

  return function (requestArgs) {
    return function (dispatch) {
      (0, _invariant2.default)(!requestArgs || isObject(requestArgs), 'requestArgs must be an Object. Received ' + (typeof requestArgs === 'undefined' ? 'undefined' : _typeof(requestArgs)));

      dispatch(startedAction({ requestArgs: requestArgs }));

      requestFn(requestArgs).then(function (resp) {
        dispatch(succeededAction({ requestArgs: requestArgs, data: toRecordFn(resp) }));
      }, function (error) {
        dispatch(failedAction({ requestArgs: requestArgs, error: error }));
      }).done();

      return;
    };
  };
};